import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Page, NavItem, LoadingMsg } from "components/common";
import { useGetLatest, useXeroClient } from "../../../hooks";
import { useQuery } from "react-query";
import { useTable, useSortBy, useRowSelect } from "react-table";
import { IndeterminateCheckbox, TableCoA } from "../../../components/react-table";
import { useTranslation, Trans } from "react-i18next";
import { keyBy, reduce, capitalize, debounce, includes } from "lodash";
import {
  Button,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Alert,
  Nav,
} from "reactstrap";
import useModal from "../../../hooks/useModal";
import { useForm, FormProvider } from "react-hook-form";
import {
  FormGroup,
  Input,
  Select,
  SubmitButton,
} from "../../../components/react-hook-form";
import { NotificationManager } from "react-notifications";
import { useParams } from "react-router";
import { useAccounts, useTaxRates } from "../../../queries";
import { exportToCSV } from "services/exportToCSV"
import { coaNotes as importantNotes } from '../../UserList/modelNames';
import * as XLSX from 'xlsx';
import ImportModal, { readFileAsync } from "components/ImportModal";
import { createPortal } from "react-dom";
import { CurrencySymbol } from "services/util";

const emptyArray = [];

const typeMapping = {
  //"BANK": "Bank",
  CURRENT: "Current Asset",
  CURRLIAB: "Current Liability",
  DEPRECIATN: "Depreciation",
  DIRECTCOSTS: "Direct Costs",
  EQUITY: "Equity",
  EXPENSE: "Expense",
  FIXED: "Fixed Asset",
  INVENTORY: "Inventory Asset",
  LIABILITY: "Liability",
  NONCURRENT: "Non-current Asset",
  OTHERINCOME: "Other Income",
  OVERHEADS: "Overhead",
  PREPAYMENT: "Prepayment",
  REVENUE: "Revenue",
  SALES: "Sales",
  TERMLIAB: "Non-current Liability",
  //"PAYGLIABILITY": "PAYG Liability",
  //"SUPERANNUATIONEXPENSE": "Superannuation Expense",
  //"SUPERANNUATIONLIABILITY": "Superannuation Liability",
  //"WAGESEXPENSE": "Wages Expense"
};

const Actions = ({ table, archive, refetch }) => {
  const {
    Modal: DeleteModal,
    toggle: toggleDeleteModal,
    isOpen: isDeleteOpen,
    setIsOpen: setIsDeleteOpen,
  } = useModal();
  const {
    Modal: ArchiveModal,
    toggle: toggleArchiveModal,
    isOpen: isArchiveOpen,
    setIsOpen: setIsArchiveOpen,
  } = useModal();
  const { control: deleteControl, handleSubmit: handleDeleteSubmit } =
    useForm();
  const { control: archiveControl, handleSubmit: handleArchiveSubmit } =
    useForm();
  const { control: restoreControl, handleSubmit: handleRestoreSubmit } =
    useForm();

  const { selectedFlatRows } = table;
  const num = selectedFlatRows.length;
  const xero = useXeroClient();
  const { t } = useTranslation();

  async function onRestore() {
    const failed = [];

    for (const i of selectedFlatRows) {
      const { AccountID } = i.original;

      try {
        await xero.patch(`accounts/${AccountID}`, {
          AccountID,
          Status: "ACTIVE",
        });
      } catch (ex) {
        console.error(ex);
        failed.push(i);
      }
    }

    if (failed.length) {
      NotificationManager.error(
        t("{{ num }} accounts failed to restore", { num: failed.length })
      );
    } else {
      NotificationManager.success(t("Accounts restored successfully"));
    }
    await refetch();
  }

  async function onArchive() {
    const failed = [];

    for (const i of selectedFlatRows) {
      const { AccountID } = i.original;

      try {
        await xero.patch(`accounts/${AccountID}`, {
          AccountID,
          Status: "ARCHIVED",
        });
      } catch (ex) {
        console.error(ex);
        failed.push(i);
      }
    }

    if (failed.length) {
      NotificationManager.error(
        t("{{ num }} accounts failed to archive", { num: failed.length })
      );
    } else {
      NotificationManager.success(t("Accounts archived successfully"));
    }
    await refetch();
    setIsArchiveOpen(false);
  }

  async function onDelete() {
    const failed = [];

    for (const i of selectedFlatRows) {
      const { AccountID } = i.original;

      try {
        await xero.delete(`accounts/${AccountID}`);
      } catch (ex) {
        console.error(ex);
        failed.push(i);
      }
    }

    if (failed.length) {
      NotificationManager.error(
        t("{{ num }} accounts failed to delete", { num: failed.length })
      );
    } else {
      NotificationManager.success(t("Accounts deleted successfully"));
    }
    await refetch();
    setIsDeleteOpen(false);
  }

  const base = (children) => <div className="mb-2">{children}</div>;

  if (archive) {
    return base(
      <SubmitButton
        color="secondary"
        size="sm"
        control={restoreControl}
        onClick={handleRestoreSubmit(onRestore)}
        disabled={!selectedFlatRows.length}
      >
        <Trans>Restore</Trans>
      </SubmitButton>
    );
  }

  return base(
    <>
      {isArchiveOpen ? (
        <ArchiveModal>
          <ModalHeader>
            <Trans>Archive Accounts</Trans>
          </ModalHeader>
          <ModalBody>
            <Trans>
              You have selected {{ num }} accounts to be archived. Are you sure?
            </Trans>
          </ModalBody>
          <ModalFooter>
            <Button color="link" onClick={toggleArchiveModal}>
              <Trans>Close</Trans>
            </Button>
            <SubmitButton
              control={archiveControl}
              onClick={handleArchiveSubmit(onArchive)}
            >
              <Trans>Confirm</Trans>
            </SubmitButton>
          </ModalFooter>
        </ArchiveModal>
      ) : null}
      {isDeleteOpen ? (
        <DeleteModal>
          <ModalHeader>
            <Trans>Delete Accounts</Trans>
          </ModalHeader>
          <ModalBody>
            <Trans>
              You have selected {{ num }} accounts to be deleted. Are you sure?
            </Trans>
          </ModalBody>
          <ModalFooter>
            <Button color="link" onClick={toggleDeleteModal}>
              <Trans>Close</Trans>
            </Button>
            <SubmitButton
              control={deleteControl}
              onClick={handleDeleteSubmit(onDelete)}
            >
              <Trans>Confirm</Trans>
            </SubmitButton>
          </ModalFooter>
        </DeleteModal>
      ) : null}
      <Button
        color="danger"
        size="sm"
        onClick={toggleDeleteModal}
        disabled={!selectedFlatRows.length}
      >
        <Trans>Delete</Trans>
      </Button>
      <Button
        color="secondary"
        className="ml-2"
        size="sm"
        onClick={toggleArchiveModal}
        disabled={!selectedFlatRows.length}
      >
        <Trans>Archive</Trans>
      </Button>
    </>
  );
};

export const EditAccount = ({
  values,
  toggle,
  currencies,
  accountTypes,
  taxRates,
  onSuccess,
}) => {
  const methods = useForm({
    defaultValues: {
      AccountID: values?.AccountID,
      Type: values?.Type,
    },
    mode: "onBlur",
  });
  const isBank = values?.Type === "BANK";
  const { handleSubmit, setError, clearErrors, formState } = methods;
  const { errors } = formState;
  const { t } = useTranslation();
  const xero = useXeroClient();

  const bankAccountTypes = useMemo(
    () => [
      {
        label: t("Bank"),
        value: "BANK",
      },
      {
        label: t("Credit Card"),
        value: "CREDITCARD",
      },
      {
        label: t("PayPal"),
        value: "PAYPAL",
      },
    ],
    [t]
  );

  async function onSubmit(x) {
    try {
      await (x.AccountID
        ? xero.put(`accounts/${x.AccountID}`, x)
        : xero.post("accounts", x));
      await onSuccess();
    } catch (ex) {
      const code = ex.response?.data?.code;

      if (code !== "ValidationError") {
        throw ex;
      }
      setError("server", {
        message: ex.response.data.detail,
      });
    }
  }

  return (
    <FormProvider {...methods}>
      <ModalHeader>
        {isBank ? (
          values.AccountID ? (
            <Trans>Edit Bank Account Details</Trans>
          ) : (
            <Trans>Add New Bank Account</Trans>
          )
        ) : values.AccountID ? (
          <Trans>Edit Account Details</Trans>
        ) : (
          <Trans>Add New Account</Trans>
        )}
      </ModalHeader>
      <ModalBody>
        {isBank ? (
          <>
            <FormGroup className="pb-2 pt-2"
              required
              label={t("Account Name")}
              name="Name"
              help={t(
                "As you would like it to appear in Xero (limited to 150 characters)"
              )}
            >
              <Input style={{backgroundColor:'#F4F7FE'}}
                defaultValue={values.Name}
                name="Name"
                className="form-control"
                rules={{ required: true, maxLength: 150 }}
              />
            </FormGroup>
            <FormGroup className="pb-2 pt-2"
              label={t("Bank Account Type")}
              name="BankAccountType"
              required
            >
              <Select style={{backgroundColor:'#F4F7FE'}}
                rules={{ required: true }}
                simpleValue
                defaultValue={values.BankAccountType}
                options={bankAccountTypes}
                name="BankAccountType"
              />
            </FormGroup>
            <FormGroup className="pb-2 pt-2"
              label={t("Code")}
              name="Code"
              help={t(
                "A unique code/number for this account (limited to 10 characters)"
              )}
            >
              <Input style={{backgroundColor:'#F4F7FE'}}
                defaultValue={values.Code}
                name="Code"
                className="form-control"
                rules={{
                  maxLength: 10,
                }}
              />
            </FormGroup>
            <FormGroup label={t("Currency")} name="CurrencyCode" required className="pb-2 pt-2">
              <Select style={{backgroundColor:'#F4F7FE'}}
                rules={{ required: true }}
                simpleValue
                defaultValue={values.CurrencyCode}
                options={currencies}
                name="CurrencyCode"
              />
            </FormGroup>
            <FormGroup className="pb-2 pt-2"
              required
              label={t("Bank Account Number")}
              name="BankAccountNumber"
            >
              <Input style={{backgroundColor:'#F4F7FE'}}
                defaultValue={values.BankAccountNumber}
                name="BankAccountNumber"
                className="form-control"
                rules={{
                  required: true,
                  maxLength: 20,
                }}
              />
            </FormGroup>
          </>
        ) : (
          <>
            <FormGroup required label={t("Account Type")} name="Type" className="pb-2 pt-2">
              <Select style={{backgroundColor:'#F4F7FE'}}
                simpleValue
                defaultValue={values.Type}
                options={accountTypes}
                name="Type"
                rules={{
                  required: true, 
                }}
              />
            </FormGroup>
            <FormGroup className="pb-2 pt-2"
              required
              label={t("Code")}
              name="Code"
              help={t(
                "A unique code/number for this account (limited to 10 characters)"
              )}
            >
              <Input style={{backgroundColor:'#F4F7FE'}}
                defaultValue={values.Code}
                name="Code"
                className="form-control"
                rules={{
                  maxLength: 10,
                  required: true,
                }}
              />
            </FormGroup>
            <FormGroup className="pb-2 pt-2"
              required
              label={t("Name")}
              name="Name"
              help={t(
                "A short title for this account (limited to 150 characters)"
              )}
            >
              <Input style={{backgroundColor:'#F4F7FE'}}
                defaultValue={values.Name}
                name="Name"
                className="form-control"
                rules={{ required: true, maxLength: 150 }}
              />
            </FormGroup>
            <FormGroup className="pb-2 pt-2"
              label={t("Description")}
              name="Description"
              help={t("A description of how this account should be used")}
            >
              <Input style={{backgroundColor:'#F4F7FE'}}
                defaultValue={values.Description}
                component="textarea"
                name="Description"
                className="form-control"
                rows={2}
              />
            </FormGroup>
            <FormGroup className="pb-2 pt-2"
              label={t("Tax")}
              name="TaxType"
              required
              help={t("The default tax setting for this account")}
            >
              <Select style={{backgroundColor:'#F4F7FE'}}
                defaultValue={values.TaxType}
                labelKey="Name"
                valueKey="TaxType"
                rules={{ required: true }}
                simpleValue
                name="TaxType"
                options={taxRates}
              />
            </FormGroup>
            {!values.SystemAccount ? (
              <>
                <FormGroup className="pb-2 pt-2"
                  check
                  label={t("Show in Expense Claims")}
                  htmlFor="ShowInExpenseClaims"
                >
                  <Input style={{backgroundColor:'#F4F7FE'}}
                    defaultValue={values?.ShowInExpenseClaims}
                    className="form-check-input"
                    type="checkbox"
                    name="ShowInExpenseClaims"
                    id="ShowInExpenseClaims"
                  />
                </FormGroup>
                <FormGroup className="pb-2 pt-2"
                  check 
                  label={t("Enable payments to this account")}
                  htmlFor="EnablePaymentsToAccount"
                  name="EnablePaymentsToAccount"
                >
                  <Input style={{backgroundColor:'#F4F7FE'}}
                    defaultValue={values?.EnablePaymentsToAccount}
                    className="form-check-input"
                    type="checkbox"
                    name="EnablePaymentsToAccount"
                    id="EnablePaymentsToAccount"
                  />
                </FormGroup>
              </>
            ) : null}
          </>
        )}

        {errors.server?.message ? (
          <Alert color="danger" className="mt-2">
            {errors.server?.message}
          </Alert>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={toggle}>
          <Trans>Close</Trans>
        </Button>
        <SubmitButton
          onClick={() => {
            clearErrors("server");
            handleSubmit(onSubmit)();
          }}
        >
          <Trans>Save</Trans>
        </SubmitButton>
      </ModalFooter>
    </FormProvider>
  );
};

const DebouncedInput = ({
  onChange: onChangeProp,
  value: valueProp,
  ...props
}) => {
  const getOnChange = useGetLatest(onChangeProp);
  const [value, setValue] = useState(valueProp);

  useEffect(() => {
    setValue(valueProp);
  }, [valueProp]);

  const debouncedOnChange = useMemo(() => {
    return debounce((e) => {
      return getOnChange()(e);
    }, 500);
  }, [getOnChange]);

  const onChange = useCallback(
    (e) => {
      e.persist();
      setValue(e.target.value);
      debouncedOnChange(e);
    },
    [debouncedOnChange, setValue]
  );

  return <input type="text" onChange={onChange} value={value} {...props} />;
};

export const ChartOfAccounts = ({ match }) => {
  const xero = useXeroClient();
  const { t } = useTranslation();
  const [editing, setEditing] = useState();
  const [globalFilter, setGlobalFilter] = useState("");
  const { Modal, setIsOpen, isOpen, toggle } = useModal();
  const { klass } = useParams();

  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const accountTypes = useMemo(
    () =>
      reduce(
        typeMapping,
        (acc, v, k) => {
          acc.push({
            label: t(v),
            value: k,
          });
          return acc;
        },
        []
      ),
    [t]
  );

  const keyedAccountTypes = useMemo(
    () =>
      reduce(
        accountTypes,
        (acc, i) => {
          acc[i.value] = i.label;
          return acc;
        },
        {}
      ),
    [accountTypes]
  );

  const { data: _accounts, refetch } = useAccounts({
    placeholderData: emptyArray,
  });

  const { data: currencies } = useQuery(
    "currencies",
    () => {
      const currencies = xero.get("currencies").get("data");
      return currencies.map((i) => ({value: i.Code, label: <CurrencySymbol currency={i.Code} />}));
    },
    {
      placeholderData: emptyArray,
    }
  );

  const accounts = useMemo(() => {
    let result = _accounts;

    if (globalFilter) {
      const terms = globalFilter.toLowerCase().split(/\s+/);
      result = result.filter((i) => {
        const search = `${i.Name}${i.Description}${i.Code}`.toLowerCase();
        for (const term of terms) {
          if (!includes(search, term)) return false;
        }
        return true;
      });
    }

    if (klass === "archive")
      return result.filter((i) => i.Status === "ARCHIVED");
    result = result.filter((i) => i.Status === "ACTIVE");
    if (klass === "all") return result;
    const k = klass.toUpperCase();
    return result.filter((i) => i.Class === k);
  }, [klass, _accounts, globalFilter]);

  const { data: taxRates } = useTaxRates({
    placeholderData: emptyArray,
  });

  function truncateWords(text) {
    const words = text.split(' ');
    if (words.length <= 4) {
      return text;
    }
    return words.slice(0, 4).join(' ') + '...';
  }

  const taxRatesByType = useMemo(() => keyBy(taxRates, "TaxType"), [taxRates]);

  const columns = useMemo(
    () => [
      {
        Header: t("Code"),
        accessor: "Code",
        noWrapCell: true,
      },
      {
        Header: t("Name"),
        accessor: "Name",
        noWrapCell: true,
        Cell: ({ value, row }, ...args) => {
          const { original } = row;
          if (original.Status === "ACTIVE") {
            const truncatedName = truncateWords(value);
            return (
              <a href="#" onClick={(e) => {
                e.preventDefault();
                setEditing(original);
                setIsOpen(true);
              }}
                  className="p-0"
                >
                  {truncatedName}
              </a>
            );
          } else {
            return value;
          }
          
         // if  (original.Description) {
         //   return (
         //       <>
         //         <br />
         //         <span className="text-muted tw-text-sm tw-max-w-sm">
         //           {original.Description}
         //         </span>
         //       </>
         //     );
         //    } else {
         //     return null;
         //   }
        },
      },
      {
        Header: t("Type"),
        id: "type",
        noWrapCell: true,
        accessor: (row) => {
          return keyedAccountTypes[row.Type] ?? t(capitalize(row.Type));
        },
      },
      {
        Header: t("Tax Rate"),
        id: "tax_rate",
        noWrapCell: true,
        accessor: (row) => {
          const rate = taxRatesByType[row.TaxType];
          if (!rate) return "";
          return rate.Name;
        },
      },
      {
        id: "xero",
        noWrapCell: true,
        Header: t("YTD"),
        Cell: ({ row }) => {
          const { original } = row;

          return (
            <a
              href={`https://go.xero.com/GeneralLedger/AccountDetails.aspx?accID=${original.AccountID}`}
            >
              {t("View in Xero")}
            </a>
          );
        },
      },
    ],
    [t, taxRatesByType, keyedAccountTypes]
  );

  const table = useTable(
    {
      data: accounts,
      columns,
      taxRates,
      noWrapHeaders: true,
      manualGlobalFilter: true,
    },
    useSortBy,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Cell: ({ row }) => {
            const { original } = row;
            if (original.SystemAccount) {
              return <span style={{ cursor:"pointer" }} className="fa fa-lock text-muted" />;
            }

            return (
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            );
          },
        },
        ...columns,
      ]);
    }
  );

  async function onSuccess() {
    NotificationManager.success(t("Account saved successfully"));
    await refetch();
    setIsOpen(false);
  }
  
  const uploadFile = async (file) => {
    try {
      const content = await readFileAsync(file);
      setIsLoading(true)
      const parsedData = parseXlsx(content);
      const iterations = Math.min(parsedData.length, 20);
      let validIterationProcessed = false;
  
      for (let i = 0; i < iterations; i++) {
        const row = parsedData[i];
  
        if (!row.Code && !row.Name) {
          continue;
        }

        try {
          const response = await xero.post('accounts', [row]);
          NotificationManager.success(t(`Account ${row.Name} uploaded successfully`));
          console.log('Data successfully:', response);
          setIsLoading(false)
          validIterationProcessed = true;
        } catch (uploadError) {
          handleUploadError(uploadError);
        }
      }

      if (validIterationProcessed) {
        setTimeout(() => {
          setIsModalOpen(false);
          refetch()
        }, 800);
    }
      if (parsedData.length > 20) {
        NotificationManager.warning(t('Exceeded the maximum allowed iterations (20). Please upload the rest again.'));
        setIsLoading(false)
      }
    } catch (error) {
      setIsLoading(false)
      handleUploadError(error);
    }
  };
  
  const parseXlsx = (xlsxContent) => {
    const data = new Uint8Array(xlsxContent);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });
  
    const headerRow = jsonData[0];
  
    return jsonData.slice(1).map((row) => {
      const expensColumn = row[headerRow.indexOf("InExpenseClaims")]
      const paymentsColumn = row[headerRow.indexOf("PaymentsToAccount")]
      const inExpense = expensColumn !== true ? undefined : expensColumn;
      const enablePayments = paymentsColumn !== true ? undefined: paymentsColumn
      return {
        Code: row[headerRow.indexOf("AccountCode*")],
        Name: row[headerRow.indexOf("AccountName*")],
        Type: row[headerRow.indexOf("Type*")],
        Description: row[headerRow.indexOf("Description")],
        TaxType: row[headerRow.indexOf("TaxCode")],
        ...(inExpense ? { ShowInExpenseClaims: inExpense } : {}),
        ...(enablePayments ? { EnablePaymentsToAccount: enablePayments } : {}),
      };
    });
  };
  
  const handleUploadError = (error) => {
    let errorCount = 0;
  
    if (error && error.response && error.response.data && error.response.data.detail) {
      error.response.data.detail.forEach((detail, index) => {
        if (errorCount < 3) {
          const errorMessage = `${index + 1}. ${detail}`;
          NotificationManager.error(errorMessage, 'Error');
          console.error(errorMessage);
          errorCount++;
        }
      });
    } else {
      if (errorCount < 3) {
        const errorMessage = 'Error uploading file, check console for more info.';
        NotificationManager.error(errorMessage);
        console.error(errorMessage);
        errorCount++;
      }
    }
  };

  const exportHandler = (data) => {

    const exportData = data.map((account) => {
      let { AccountID, ...rest } = account

      return rest
    })

    exportToCSV({ data: exportData, filename: "Chart of Accounts" })
  }

  return (
    <Page title="Chart of accounts" className="inv-tabs">
      {isLoading &&
        createPortal(
        <div className="content">
          <div className="loader"></div>
            {<LoadingMsg/> }
          </div>,
        document.querySelector('#loader-holder')
      )}
      <div div className="d-flex align-items-center" style={{paddingBottom:20}}>
        <Button
          color="primary"
          className="mr-2"
          onClick={() => {
            setEditing({
              Code: "",
              Name: "",
              Type: null,
              Description: "",
              TaxType: null,
              ShowInExpenseClaims: false,
              EnablePaymentsToAccount: false,
            });
            setIsOpen(true);
          }}
        >
          <span className="fa fa-plus mr-2" />
          <Trans>Add Account</Trans>
        </Button>
        <Button
          color="primary"
          onClick={() => {
            setEditing({
              Code: "",
              Name: "",
              Type: "BANK",
              BankAccountNumber: "",
              CurrencyCode: null,
              BankAccountType: "BANK",
            });
            setIsOpen(true);
          }}
        >
          <span className="fa fa-plus mr-2" />
          <Trans>Add Bank Account</Trans>
        </Button>
        <Button color="primary" onClick={openModal}>
          <i className="fas fa-file-excel"/><span className="mx-1" /><Trans>Import Accounts</Trans>
        </Button>
        <ImportModal
          isOpen={isModalOpen}
          toggle={closeModal}
          title="Accounts"
          importantNotes={importantNotes}
          handleUpload={uploadFile} 
        />
        <div className="mx-auto" />
        <div >
          <Button onClick={() => exportHandler(accounts)} color="primary">
            <i className="fas fa-file-excel mr-2"></i><Trans>Export Accounts</Trans>
          </Button>
        </div>
      </div>
      {isOpen ? (
        <Modal size="lg">
          <EditAccount
            currencies={currencies}
            onSuccess={onSuccess}
            accountTypes={accountTypes}
            taxRates={taxRates}
            toggle={toggle}
            values={editing}
          />
        </Modal>
      ) : null}
      <Nav tabs className="mb-4">
        <NavItem exact to={`all`}>
          All Accounts
        </NavItem>
        <NavItem to={`asset`}>Assets</NavItem>
        <NavItem to={`liability`}>Liabilities</NavItem>
        <NavItem to={`equity`}>Equity</NavItem>
        <NavItem to={`expense`}>Expenses</NavItem>
        <NavItem to={`revenue`}>Revenue</NavItem>
        <NavItem to={`archive`}>Archive</NavItem>
      </Nav>
      <DebouncedInput
        className="form-control mb-4"
        placeholder={t("Search...")}
        value={globalFilter}
        onChange={(e) => {
          setGlobalFilter(e.target.value);
        }}
      />
      <Actions archive={klass === "archive"} refetch={refetch} table={table} />
      <TableCoA table={table} className="inv-table no-wrap v-middle text-center" responsive/>
    </Page>
  );
};

export default EditAccount;